import { strToU8, unzipSync, zipSync } from "fflate";
import { stopSimulation, startSimulationFromXmlFile } from "../physics/mujocoBridge";
import { useFileStore } from "../stores/fileStore";
import {
  restoreGeomPairingsFromConfig,
  restoreVisibilityOverridesFromConfig,
  restoreVisibilitySettingsFromConfig,
} from "../stores/fileStore/sceneConfigImport";
import { getProjectSaveActions } from "../stores/projectSaveStore";
import { getHistoryActions } from "../stores/historyStore";
import {
  getAssetActions,
  getAssetById,
  getAssetEntries,
  type AssetRegistration,
} from "../stores/assetStore";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { useViewerStore } from "../stores/viewerStore";
import { getJointMarkerScalePercent } from "../selected-joint-visibility";
import * as RuntimeRegistry from "../runtime/objectRegistry";
import { getViewerHandles } from "../runtime/viewerHandles";
import { captureCameraHome } from "../runtime/cameraHome";
import { collectResolvedXmlAssetsForZip } from "./environmentZipAssets";
import { getFloorAppearanceOptions } from "./floorAppearanceOptions";
import { isSafeRelativeArchivePath } from "./archivePaths";
import { readZipFileWithinBrowserImportLimits } from "./importLimits";
import { findBodyForJointUuid } from "./bodyHierarchy";
import {
  isPhysicsRuntimeAuthoringLocked,
  shouldBlockSceneAuthoringMutation,
} from "./physicsGuards";
import { buildProjectSaveSnapshotHash } from "./projectSaveSnapshot";
import { migrateSceneConfig } from "../core/migrations/sceneConfig";
import { indexUploadedMeshFiles } from "../mesh-editor/uploadIndex";
import {
  buildProjectSnapshot,
  CRITTER_PROJECT_SNAPSHOT_FILENAME,
  migrateProjectSnapshot,
  type CritterProjectSnapshotV1,
} from "../core/migrations/projectSnapshot";
import {
  buildProjectManifest,
  CRITTER_PROJECT_MANIFEST_FILENAME,
  migrateProjectManifest,
  type CritterProjectAssetManifestEntryV1,
  type CritterProjectIntegrityReasonV1,
} from "../core/migrations/projectManifest";
import { showWebCryptoUnavailableToast } from "./browserCapabilityToasts";
import { logger } from "./logger";
import { putBytesMany, saveManifest } from "./idb";
import { isAssetPersistenceEnabled } from "./assetStoragePreference";
import type { FileInputChangeEvent } from "../stores/fileStore/types";

const APP_VERSION = (import.meta.env?.VITE_APP_VERSION as string | undefined) ?? "0.0.0";

type CameraOrbitConfig = {
  camera?: {
    position?: unknown;
    quaternionXyzw?: unknown;
    fov?: unknown;
    zoom?: unknown;
    near?: unknown;
    far?: unknown;
  };
  orbit?: {
    target?: unknown;
  };
};
type SceneBodyLike = {
  uuid?: unknown;
  name?: unknown;
  position?: unknown;
  orientation?: unknown;
  children?: unknown;
  geoms?: SceneGeomLike[];
};
type SceneJointLike = {
  uuid?: unknown;
  name?: unknown;
  kind?: unknown;
  ref?: unknown;
  quat?: unknown;
};
type SceneGeomLike = {
  uuid?: unknown;
  name?: unknown;
  pairedGeomUUID?: unknown;
};
type SceneEntityLike = {
  uuid?: unknown;
  name?: unknown;
};
type SceneCameraLike = SceneEntityLike & {
  kind?: unknown;
  position?: unknown;
  orientation?: unknown;
  fovy?: unknown;
  width?: unknown;
  height?: unknown;
  fps?: unknown;
  mountType?: unknown;
  mountBodyUuid?: unknown;
  mountSiteUuid?: unknown;
};
type SceneConfigSource = {
  bodies?: Record<string, SceneBodyLike | undefined>;
  joints?: Record<string, SceneJointLike | undefined>;
  sites?: Record<string, SceneEntityLike | undefined>;
  actuators?: Record<string, SceneEntityLike | undefined>;
  meshes?: Record<string, SceneEntityLike | undefined>;
  cameras?: Record<string, SceneCameraLike | undefined>;
  worldBodies?: Array<string | { uuid?: unknown; name?: unknown }>;
};
type InitPoseBody = SceneBodyLike & { uuid: unknown };
type InitPoseJoint = SceneJointLike & { uuid: unknown };
type FileWithRelativePath = File & { webkitRelativePath?: string };
type FindBodyForJointBodyMap = Parameters<typeof findBodyForJointUuid>[0];

const asCameraConfig = (value: unknown): CameraOrbitConfig["camera"] =>
  value && typeof value === "object" ? (value as CameraOrbitConfig["camera"]) : undefined;

const asOrbitConfig = (value: unknown): CameraOrbitConfig["orbit"] =>
  value && typeof value === "object" ? (value as CameraOrbitConfig["orbit"]) : undefined;

export class ProjectSha256UnavailableError extends Error {
  readonly reason = "sha256-unavailable" as const;

  constructor() {
    super("SHA-256 hashing is unavailable in this context. Use HTTPS or localhost.");
    this.name = "ProjectSha256UnavailableError";
  }
}

export class ProjectAssetReadError extends Error {
  readonly reason = "asset-read-error" as const;
  readonly cause: unknown;

  constructor(assetPath: string, cause: unknown) {
    super(`Could not read project asset "${assetPath}" while preparing the project archive.`);
    this.name = "ProjectAssetReadError";
    this.cause = cause;
  }
}

export class ProjectAssetIntegrityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProjectAssetIntegrityError";
  }
}

export class ProjectMissingMeshAssetsError extends Error {
  readonly missingAssets: string[];

  constructor(missingAssets: string[]) {
    const sample = missingAssets.slice(0, 6).join(", ");
    const extra = missingAssets.length > 6 ? `, and ${missingAssets.length - 6} more` : "";
    super(
      `Project is missing ${missingAssets.length} mesh asset registration(s): ${sample}${extra}`
    );
    this.name = "ProjectMissingMeshAssetsError";
    this.missingAssets = missingAssets;
  }
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function toSafeZipPath(input: string) {
  let p = String(input || "");
  p = p.replace(/\\/g, "/");
  p = p.replace(/^\/*/, "");
  p = p.replace(/:/g, "_");
  while (p.includes("../")) p = p.replaceAll("../", "");
  while (p.includes("..\\")) p = p.replaceAll("..\\", "");
  return p || "asset";
}

function classifyAssetRole(path: string): CritterProjectAssetManifestEntryV1["role"] {
  const lower = String(path || "").toLowerCase();
  if (lower.endsWith(".obj") || lower.endsWith(".stl")) return "mesh";
  if (
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".bmp") ||
    lower.endsWith(".tga") ||
    lower.endsWith(".webp") ||
    lower.endsWith(".ktx") ||
    lower.endsWith(".ktx2")
  ) {
    return "texture";
  }
  if (lower.endsWith(".mtl")) return "sidecar";
  return "asset";
}

async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    showWebCryptoUnavailableToast("Project bundle");
    throw new ProjectSha256UnavailableError();
  }
  const buffer =
    bytes instanceof Uint8Array
      ? (bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer)
      : bytes;
  const digest = await globalThis.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

async function buildAssetManifestEntry(
  asset: Awaited<ReturnType<typeof collectResolvedXmlAssetsForZip>>[number],
  bytes: ArrayBuffer,
  options?: {
    allowUnverifiableLocalProject?: boolean;
    unverifiableReason?: CritterProjectIntegrityReasonV1;
  }
): Promise<CritterProjectAssetManifestEntryV1> {
  let sha256: string | undefined;
  try {
    // Hash the original asset bytes before zip compression. project.json hashes are
    // over the logical asset file, not whatever compressed bytes fflate may emit.
    sha256 = await sha256Hex(bytes);
  } catch (error) {
    if (!options?.allowUnverifiableLocalProject) throw error;
  }
  return {
    path: asset.zipPath,
    role: classifyAssetRole(asset.zipPath),
    referencedBy: ["model.xml"],
    byteLength: bytes.byteLength,
    ...(sha256 ? { sha256 } : {}),
  };
}

export async function validateProjectManifestAssets(args: {
  projectManifest: NonNullable<ReturnType<typeof migrateProjectManifest>>;
  fileByNormalizedName: Map<string, File>;
}): Promise<void> {
  const requiresSha256 = args.projectManifest.integrity.policy === "sha256-required";
  if (requiresSha256 && !args.projectManifest.integrity.complete) {
    throw new ProjectAssetIntegrityError(
      "Project manifest requires SHA-256 integrity but is marked incomplete."
    );
  }

  if (requiresSha256 || args.projectManifest.integrity.complete) {
    const assetMissingHash = args.projectManifest.assets.find((asset) => !asset.sha256);
    if (assetMissingHash) {
      throw new ProjectAssetIntegrityError(
        `Asset corrupted: ${assetMissingHash.path} project manifest requires SHA-256 but is missing SHA-256`
      );
    }
  }

  for (const asset of args.projectManifest.assets) {
    const normalized = normalizeBundlePath(asset.path).toLowerCase();
    const file = args.fileByNormalizedName.get(normalized);
    if (!file) {
      throw new ProjectAssetIntegrityError(`Asset missing: ${asset.path}`);
    }
    if (file.size !== asset.byteLength) {
      throw new ProjectAssetIntegrityError(
        `Asset truncated: ${asset.path} expected ${asset.byteLength} bytes, got ${file.size}`
      );
    }
    if (!asset.sha256) continue;
    let bytes: ArrayBuffer;
    try {
      bytes = await file.arrayBuffer();
    } catch (error) {
      throw new ProjectAssetReadError(asset.path, error);
    }
    // Validate decompressed archive entry bytes. project.json hashes are not over
    // zip-compressed data, so this remains stable if the archive compression level changes.
    const actualSha256 = await sha256Hex(bytes);
    if (actualSha256.toLowerCase() !== asset.sha256.toLowerCase()) {
      throw new ProjectAssetIntegrityError(`Asset corrupted: ${asset.path} SHA-256 mismatch`);
    }
  }
}

function warnIfProjectManifestIntegrityIsLocalOnly(
  projectManifest: NonNullable<ReturnType<typeof migrateProjectManifest>> | null
) {
  if (!projectManifest) return;
  if (
    projectManifest.integrity.policy === "sha256-required" &&
    projectManifest.integrity.complete
  ) {
    return;
  }
  logger.warn("[environmentBundle] Loaded local-only project asset integrity.", {
    policy: projectManifest.integrity.policy,
    complete: projectManifest.integrity.complete,
    reason: projectManifest.integrity.complete ? undefined : projectManifest.integrity.reason,
  });
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("critter-project-integrity", {
      detail: {
        policy: projectManifest.integrity.policy,
        complete: projectManifest.integrity.complete,
        reason: projectManifest.integrity.complete ? undefined : projectManifest.integrity.reason,
        assetCount: projectManifest.assets.length,
        missingSha256Count: projectManifest.assets.filter((asset) => !asset.sha256).length,
      },
    })
  );
}

function finiteVector(values: unknown, length: number, fallback: number[]) {
  const arr = Array.isArray(values) ? values.map(Number) : [];
  return arr.length === length && arr.every(Number.isFinite) ? arr : fallback;
}

function toFiniteVec(values: unknown, length: number): number[] | null {
  if (!Array.isArray(values) || values.length !== length) return null;
  const nums = values.map(Number);
  return nums.every(Number.isFinite) ? nums : null;
}

function applyCameraOrbitConfig(cfg: CameraOrbitConfig | null): void {
  if (!cfg) return;

  const attemptApply = (): boolean => {
    const { camera, controls } = getViewerHandles();
    const cameraCfg = cfg.camera || null;
    const orbitCfg = cfg.orbit || null;
    let appliedAny = false;

    if (camera && cameraCfg) {
      const position = toFiniteVec(cameraCfg.position, 3);
      const quaternionXyzw = toFiniteVec(cameraCfg.quaternionXyzw, 4);
      if (position) {
        camera.position?.set(position[0], position[1], position[2]);
        appliedAny = true;
      }
      if (quaternionXyzw) {
        camera.quaternion?.set(
          quaternionXyzw[0],
          quaternionXyzw[1],
          quaternionXyzw[2],
          quaternionXyzw[3]
        );
        appliedAny = true;
      }
      const fov = Number(cameraCfg.fov);
      const zoom = Number(cameraCfg.zoom);
      const near = Number(cameraCfg.near);
      const far = Number(cameraCfg.far);
      if (Number.isFinite(fov) && fov > 0) {
        camera.fov = fov;
        appliedAny = true;
      }
      if (Number.isFinite(zoom) && zoom > 0) {
        camera.zoom = zoom;
        appliedAny = true;
      }
      if (Number.isFinite(near) && near > 0) {
        camera.near = near;
        appliedAny = true;
      }
      if (Number.isFinite(far) && far > 0) {
        camera.far = far;
        appliedAny = true;
      }
      if (appliedAny) camera.updateProjectionMatrix?.();
    }

    if (controls && orbitCfg) {
      const target = toFiniteVec(orbitCfg.target, 3);
      if (target && controls.target?.set) {
        controls.target.set(target[0], target[1], target[2]);
        controls.update?.();
        appliedAny = true;
      }
    }

    if (appliedAny) captureCameraHome();
    return appliedAny;
  };

  let tries = 0;
  const tick = () => {
    tries += 1;
    if (attemptApply() || tries >= 12) return;
    setTimeout(tick, 50);
  };
  setTimeout(tick, 0);
}

function liveBodyPose(body: SceneBodyLike) {
  const registry = RuntimeRegistry.getAll() || {};
  const obj = body?.uuid ? registry[String(body.uuid)] : null;
  if (!obj?.position || !obj?.quaternion) return null;
  const position = [Number(obj.position.x), Number(obj.position.y), Number(obj.position.z)];
  const orientation = [
    Number(obj.quaternion.w),
    Number(obj.quaternion.x),
    Number(obj.quaternion.y),
    Number(obj.quaternion.z),
  ];
  if (!position.every(Number.isFinite) || !orientation.every(Number.isFinite)) return null;
  return { position, orientation };
}

function buildInitPoseConfig(scene: SceneConfigSource) {
  const bodies = Object.values(scene.bodies || {}).filter(Boolean) as InitPoseBody[];
  const joints = Object.values(scene.joints || {}).filter(Boolean) as InitPoseJoint[];
  const freeJointBodyUuids = new Set<string>();
  joints.forEach((joint) => {
    if (joint?.kind !== "free" || !joint.uuid) return;
    const bodyUuid = findBodyForJointUuid(
      (scene.bodies || {}) as unknown as FindBodyForJointBodyMap,
      String(joint.uuid)
    );
    if (bodyUuid) freeJointBodyUuids.add(String(bodyUuid));
  });

  const bodyPoses: Record<string, { name: string; position: number[]; orientation: number[] }> = {};
  bodies.forEach((body) => {
    if (!body?.uuid) return;
    const livePose = freeJointBodyUuids.has(String(body.uuid)) ? liveBodyPose(body) : null;
    const position = livePose?.position || finiteVector(body.position, 3, [0, 0, 0]);
    const orientation = livePose?.orientation || finiteVector(body.orientation, 4, [1, 0, 0, 0]);
    bodyPoses[String(body.uuid)] = {
      name: String(body.name || ""),
      position,
      orientation,
    };
  });

  const jointPoses: Record<
    string,
    | { name: string; kind: "hinge" | "slide"; value: number }
    | { name: string; kind: "ball"; quat: number[] }
  > = {};
  joints.forEach((joint) => {
    if (!joint?.uuid) return;
    if (joint.kind === "hinge" || joint.kind === "slide") {
      jointPoses[String(joint.uuid)] = {
        name: String(joint.name || ""),
        kind: joint.kind,
        value: Number.isFinite(Number(joint.ref)) ? Number(joint.ref) : 0,
      };
      return;
    }
    if (joint.kind === "ball") {
      jointPoses[String(joint.uuid)] = {
        name: String(joint.name || ""),
        kind: "ball",
        quat: finiteVector(joint.quat, 4, [1, 0, 0, 0]),
      };
    }
  });

  return {
    init: {
      bodies: bodyPoses,
      joints: jointPoses,
    },
  };
}

const hiddenVisibilityEntries = (record: Record<string, boolean> | undefined) =>
  Object.fromEntries(
    Object.entries(record || {}).filter(([, visible]) => visible === false)
  ) as Record<string, boolean>;

export function buildSceneConfig() {
  const scene = getSceneState() as SceneConfigSource;
  const viewer = useViewerStore.getState();
  const bodies = Object.values(scene.bodies || {}).filter(Boolean) as SceneBodyLike[];
  const joints = Object.values(scene.joints || {}).filter(Boolean) as SceneJointLike[];
  const sites = Object.values(scene.sites || {}).filter(Boolean) as SceneEntityLike[];
  const actuators = Object.values(scene.actuators || {}).filter(Boolean) as SceneEntityLike[];
  const meshes = Object.values(scene.meshes || {}).filter(Boolean) as SceneEntityLike[];
  const worldBodies = Array.isArray(scene.worldBodies) ? scene.worldBodies : [];
  const cameras = Object.values(scene.cameras || {}).filter(Boolean) as SceneCameraLike[];
  const geomPairings: Array<{
    bodyUuid: string;
    bodyName: string;
    bodyIndex: number;
    geomIndex: number;
    pairedGeomIndex: number;
    geomUuid: string;
    pairedGeomUuid: string;
    geomName: string;
    pairedGeomName: string;
  }> = [];
  const bodyOrder = new Map<string, number>();

  const visitBody = (uuid: string) => {
    if (!uuid || bodyOrder.has(uuid)) return;
    const body = scene.bodies?.[uuid];
    if (!body) return;
    bodyOrder.set(uuid, bodyOrder.size);
    const children = Array.isArray(body.children) ? body.children : [];
    children.forEach((childUuid) => visitBody(String(childUuid || "")));
  };

  worldBodies.forEach((wb) => {
    const uuid = typeof wb === "string" ? wb : wb?.uuid;
    visitBody(String(uuid || ""));
  });
  bodies.forEach((body) => visitBody(String(body?.uuid || "")));

  bodies.forEach((body) => {
    const geoms = Array.isArray(body?.geoms) ? body.geoms : [];
    const geomIndexByUuid = new Map(
      geoms
        .map((geom, index) => (geom?.uuid ? [String(geom.uuid), index] : null))
        .filter(Boolean) as [string, number][]
    );
    const seen = new Set<string>();

    geoms.forEach((geom, index) => {
      const partnerUuid = geom?.pairedGeomUUID ? String(geom.pairedGeomUUID) : "";
      if (!partnerUuid) return;
      const partnerIndex = geomIndexByUuid.get(partnerUuid);
      if (partnerIndex === undefined || partnerIndex === index) return;
      const key = [index, partnerIndex].sort((a, b) => a - b).join(":");
      if (seen.has(key)) return;
      seen.add(key);

      const partner = geoms[partnerIndex] || {};
      geomPairings.push({
        bodyUuid: String(body.uuid || ""),
        bodyName: String(body.name || ""),
        bodyIndex: bodyOrder.get(String(body.uuid || "")) ?? -1,
        geomIndex: index,
        pairedGeomIndex: partnerIndex,
        geomUuid: String(geom.uuid || ""),
        pairedGeomUuid: String(partner.uuid || partnerUuid),
        geomName: String(geom.name || ""),
        pairedGeomName: String(partner.name || ""),
      });
    });
  });

  // Viewer continuity (not part of MJCF): camera + orbit controls.
  const { camera, controls } = getViewerHandles();
  const cameraConfig =
    camera && camera.position && camera.quaternion
      ? {
          position: [
            Number(camera.position.x),
            Number(camera.position.y),
            Number(camera.position.z),
          ],
          // THREE.Quaternion is xyzw (NOT wxyz).
          quaternionXyzw: [
            Number(camera.quaternion.x),
            Number(camera.quaternion.y),
            Number(camera.quaternion.z),
            Number(camera.quaternion.w),
          ],
          fov: Number(camera.fov),
          zoom: Number(camera.zoom),
          near: Number(camera.near),
          far: Number(camera.far),
        }
      : null;
  const orbitConfig =
    controls && controls.target
      ? {
          target: [Number(controls.target.x), Number(controls.target.y), Number(controls.target.z)],
        }
      : null;

  return {
    type: "critterapp-scene-config",
    schemaVersion: 1,
    createdAtIso: new Date().toISOString(),
    camera: cameraConfig,
    orbit: orbitConfig,
    poses: buildInitPoseConfig(scene),
    naming: {
      bodies: bodies.map((b) => ({ uuid: String(b.uuid || ""), label: String(b.name || "") })),
      actuators: actuators.map((a) => ({ uuid: String(a.uuid || ""), name: String(a.name || "") })),
      joints: joints.map((j) => ({ uuid: String(j.uuid || ""), name: String(j.name || "") })),
      sites: sites.map((s) => ({ uuid: String(s.uuid || ""), name: String(s.name || "") })),
      meshes: meshes.map((m) => ({ uuid: String(m.uuid || ""), name: String(m.name || "") })),
      worldBodies: worldBodies
        .map((wb) => {
          const uuid = typeof wb === "string" ? wb : wb?.uuid;
          const name = typeof wb === "string" ? null : wb?.name;
          if (!uuid) return null;
          return { uuid: String(uuid), name: String(name || "") };
        })
        .filter(Boolean),
    },
    visibilityOverrides: {
      bodyVisibility: hiddenVisibilityEntries(viewer.bodyVisibility),
      meshVisibility: hiddenVisibilityEntries(viewer.meshVisibility),
      collisionGeomVisibility: hiddenVisibilityEntries(viewer.collisionGeomVisibility),
    },
    visibilitySettings: {
      jointMarkerScalePercent: getJointMarkerScalePercent(),
      floorAppearance: getFloorAppearanceOptions(),
    },
    geomPairings,
    cameraProbe: {
      savedCameras: cameras.map((cam) => ({
        uuid: String(cam?.uuid || ""),
        name: String(cam?.name || ""),
        kind: String(cam?.kind || "fixed") === "mounted" ? "mounted" : "fixed",
        position: Array.isArray(cam?.position)
          ? cam.position.map((v) => Number(v) || 0)
          : [0, 0.5, 2],
        orientation: Array.isArray(cam?.orientation)
          ? [
              Number(cam.orientation[0]) || 1,
              Number(cam.orientation[1]) || 0,
              Number(cam.orientation[2]) || 0,
              Number(cam.orientation[3]) || 0,
            ]
          : [1, 0, 0, 0],
        fovy: Number.isFinite(Number(cam?.fovy)) ? Number(cam.fovy) : 65,
        width: Number.isFinite(Number(cam?.width))
          ? Math.max(32, Math.floor(Number(cam.width)))
          : 256,
        height: Number.isFinite(Number(cam?.height))
          ? Math.max(32, Math.floor(Number(cam.height)))
          : 256,
        fps: Number.isFinite(Number(cam?.fps))
          ? Math.max(1, Math.min(60, Math.floor(Number(cam.fps))))
          : 8,
        mountType: String(cam?.mountType || "body") === "site" ? "site" : "body",
        mountBodyUuid: cam?.mountBodyUuid ? String(cam.mountBodyUuid) : undefined,
        mountSiteUuid: cam?.mountSiteUuid ? String(cam.mountSiteUuid) : undefined,
      })),
      notes:
        "Camera Probe presets used by web rendering pipelines (includes non-MJCF fields like width/height/fps).",
    },
  };
}

export function buildCurrentProjectSnapshot(args?: {
  modelXmlPath?: string | null;
}): CritterProjectSnapshotV1 {
  const fileState = useFileStore.getState();
  return buildProjectSnapshot({
    scene: getSceneState(),
    projectName: fileState.projectName || "Untitled Scene",
    modelXmlPath: args?.modelXmlPath ?? "model.xml",
    referencedFiles: fileState.referencedFiles || [],
    keyframes: fileState.keyframes || {},
    keyframeDetails: fileState.keyframeDetails || {},
    preservedMjcfSections: fileState.preservedMjcfSections || {},
  });
}

// Used for paths that already came from a bundle/archive or browser File names.
// Folder-drop paths are sanitized later with toSafeZipPath before being rewrapped.
function normalizeBundlePath(input: string) {
  let p = String(input || "");
  p = p.replace(/\\/g, "/");
  p = p.replace(/^\/*/, "");
  return p;
}

function findFileMapEntryByBundlePath(fileMap: Record<string, File>, path: string): File | null {
  const normalizedPath = normalizeBundlePath(path).toLowerCase();
  if (!normalizedPath) return null;
  for (const [key, file] of Object.entries(fileMap || {})) {
    if (normalizeBundlePath(key).toLowerCase() === normalizedPath) return file;
  }
  return null;
}

function collectProjectSceneMeshFingerprints(scene: unknown): string[] {
  const sceneRecord = scene && typeof scene === "object" ? (scene as Record<string, unknown>) : {};
  const meshes =
    sceneRecord.meshes && typeof sceneRecord.meshes === "object"
      ? (sceneRecord.meshes as Record<string, { fingerprint?: unknown } | undefined>)
      : {};
  const bodies =
    sceneRecord.bodies && typeof sceneRecord.bodies === "object"
      ? (sceneRecord.bodies as Record<string, { geoms?: Array<{ mesh?: unknown }> } | undefined>)
      : {};
  const referencedMeshUuids = new Set<string>();
  Object.values(bodies).forEach((body) => {
    if (!Array.isArray(body?.geoms)) return;
    body.geoms.forEach((geom) => {
      if (typeof geom?.mesh === "string" && geom.mesh.trim()) {
        referencedMeshUuids.add(geom.mesh.trim());
      }
    });
  });

  const fingerprints = new Set<string>();
  referencedMeshUuids.forEach((meshUuid) => {
    const fingerprint = meshes[meshUuid]?.fingerprint;
    if (typeof fingerprint === "string" && fingerprint.trim()) {
      fingerprints.add(fingerprint.trim());
    }
  });
  return Array.from(fingerprints).sort();
}

async function registerProjectMeshAssetsFromManifest(args: {
  requiredFingerprints: string[];
  projectManifest: NonNullable<ReturnType<typeof migrateProjectManifest>> | null;
  fileByNormalizedName: Map<string, File>;
  persistAssets?: boolean;
}): Promise<void> {
  const missingBeforeRegistration = args.requiredFingerprints.filter(
    (fingerprint) => !getAssetById(fingerprint)
  );
  if (missingBeforeRegistration.length === 0 || !args.projectManifest) return;

  const meshAssetById = new Map<string, CritterProjectAssetManifestEntryV1>();
  args.projectManifest.assets.forEach((asset) => {
    if (asset.role !== "mesh" || !asset.sha256) return;
    meshAssetById.set(`mesh:sha256:${asset.sha256.toLowerCase()}`, asset);
  });

  const entries: AssetRegistration[] = [];
  const idbWrites: Array<{ key: string; value: File }> = [];
  const fallbackBlobUrls: Array<{ id: string; url: string }> = [];

  missingBeforeRegistration.forEach((fingerprint) => {
    const asset = meshAssetById.get(fingerprint.toLowerCase());
    if (!asset?.sha256) return;
    const file = args.fileByNormalizedName.get(normalizeBundlePath(asset.path).toLowerCase());
    if (!file) return;
    idbWrites.push({ key: asset.sha256.toLowerCase(), value: file });
  });

  let idbOk = false;
  if (
    args.persistAssets !== false &&
    idbWrites.length > 0 &&
    typeof indexedDB !== "undefined" &&
    isAssetPersistenceEnabled()
  ) {
    try {
      await putBytesMany(idbWrites);
      idbOk = true;
    } catch (error) {
      logger.warn("[environmentBundle] Failed to persist project mesh assets to IndexedDB.", error);
    }
  }

  idbWrites.forEach(({ key, value }) => {
    const asset = args.projectManifest?.assets.find(
      (entry) => entry.role === "mesh" && entry.sha256?.toLowerCase() === key.toLowerCase()
    );
    const id = `mesh:sha256:${key}`;
    const fallbackUrl =
      !idbOk && typeof URL !== "undefined" && typeof URL.createObjectURL === "function"
        ? URL.createObjectURL(value)
        : "";
    if (fallbackUrl) fallbackBlobUrls.push({ id, url: fallbackUrl });
    if (!idbOk && !fallbackUrl) return;
    entries.push({
      id,
      kind: "mesh",
      name: asset?.path || value.name || id,
      status: "idle",
      bytes: value.size,
      hash: key,
      sources: idbOk ? { idbKey: key } : fallbackUrl ? { url: fallbackUrl } : {},
    });
  });

  if (entries.length === 0) return;
  getAssetActions().registerAssets(entries);

  if (idbOk) {
    try {
      const persistable = Object.values(getAssetEntries() || {}).filter((entry) => {
        const sourceUrl = entry?.sources?.url || "";
        return !sourceUrl.startsWith("blob:");
      });
      await saveManifest(persistable);
    } catch (error) {
      logger.warn("[environmentBundle] Failed to save project mesh asset manifest.", error);
    }
  }

  if (fallbackBlobUrls.length > 0) {
    logger.debug("[environmentBundle] Project mesh assets are using session blob URLs.", {
      count: fallbackBlobUrls.length,
    });
  }
}

export async function validateAndRegisterProjectMeshAssets(args: {
  scene: unknown;
  projectManifest: NonNullable<ReturnType<typeof migrateProjectManifest>> | null;
  fileByNormalizedName: Map<string, File>;
  persistAssets?: boolean;
}): Promise<string[]> {
  const requiredFingerprints = collectProjectSceneMeshFingerprints(args.scene);
  await registerProjectMeshAssetsFromManifest({
    requiredFingerprints,
    projectManifest: args.projectManifest,
    fileByNormalizedName: args.fileByNormalizedName,
    persistAssets: args.persistAssets,
  });
  return requiredFingerprints.filter((fingerprint) => !getAssetById(fingerprint));
}

export async function readProjectCreatedAtIsoFromFileMap(
  fileMap: Record<string, File> | null | undefined
): Promise<string | undefined> {
  if (!fileMap) return undefined;
  const manifestFile =
    findFileMapEntryByBundlePath(fileMap, CRITTER_PROJECT_MANIFEST_FILENAME) ||
    Object.entries(fileMap).find(([key]) =>
      normalizeBundlePath(key).toLowerCase().endsWith(`/${CRITTER_PROJECT_MANIFEST_FILENAME}`)
    )?.[1] ||
    null;
  if (!manifestFile) return undefined;
  try {
    const manifest = migrateProjectManifest(JSON.parse(await manifestFile.text()));
    return manifest?.createdAtIso || undefined;
  } catch (error) {
    logger.warn("[environmentBundle] Failed to read createdAtIso from project.json.", error);
    return undefined;
  }
}

function stripSingleTopLevelDir(paths: string[]) {
  const normalized = paths.map(normalizeBundlePath).filter(Boolean);
  if (!normalized.length) return paths;

  const firstSegs = normalized.map((p) => p.split("/")[0]).filter(Boolean);
  const seg0 = firstSegs[0];
  const allShareSeg0 =
    Boolean(seg0) &&
    firstSegs.length === normalized.length &&
    firstSegs.every((s) => s === seg0) &&
    normalized.every((p) => p.includes("/"));

  if (!allShareSeg0) return paths;
  return normalized.map((p) => p.split("/").slice(1).join("/"));
}

async function loadEnvironmentFiles(
  files: File[],
  options?: {
    startPhysics?: boolean;
    preserveActuatorControls?: boolean;
    persistAssets?: boolean;
    allowReadOnlyLoad?: boolean;
  }
): Promise<{ name: string; files: number }> {
  if (
    options?.allowReadOnlyLoad
      ? isPhysicsRuntimeAuthoringLocked()
      : shouldBlockSceneAuthoringMutation("loadEnvironmentFiles")
  ) {
    return { name: "environment", files: 0 };
  }
  indexUploadedMeshFiles(files);
  // Stop physics so the worker/model doesn't fight the rebuild.
  try {
    await stopSimulation({ restoreScene: false });
  } catch (error) {
    logger.warn("[environmentBundle] Failed to stop physics before loading environment.", error);
  }

  // Clear fileStore so stale config/assets don't leak across loads.
  // `handleFiles` merges into the existing fileMap, so we reset first.
  try {
    const fileActions = useFileStore.getState().actions;
    fileActions.setXmlFile(null);
    fileActions.setFileMap({});
  } catch (error) {
    logger.warn(
      "[environmentBundle] Failed to clear file store before loading environment.",
      error
    );
  }

  const fileByNormalizedName = new Map<string, File>();
  files.forEach((file) => {
    const normalized = normalizeBundlePath(file.name).toLowerCase();
    if (normalized && !fileByNormalizedName.has(normalized)) {
      fileByNormalizedName.set(normalized, file);
    }
    const baseName = normalized.split("/").pop() || normalized;
    if (baseName && !fileByNormalizedName.has(baseName)) fileByNormalizedName.set(baseName, file);
  });

  const projectManifestFile = fileByNormalizedName.get(CRITTER_PROJECT_MANIFEST_FILENAME);
  let projectManifest: ReturnType<typeof migrateProjectManifest> = null;
  if (projectManifestFile) {
    try {
      projectManifest = migrateProjectManifest(JSON.parse(await projectManifestFile.text()));
    } catch (error) {
      logger.warn("[environmentBundle] Failed to parse project.json.", error);
    }
    if (!projectManifest) {
      throw new Error("Critter project manifest is missing or uses an unsupported schema version.");
    }
  }

  const projectSnapshotPath =
    projectManifest?.files.projectSnapshot || CRITTER_PROJECT_SNAPSHOT_FILENAME;
  const projectSnapshotFile =
    fileByNormalizedName.get(normalizeBundlePath(projectSnapshotPath).toLowerCase()) ||
    fileByNormalizedName.get(CRITTER_PROJECT_SNAPSHOT_FILENAME);
  if (projectSnapshotFile) {
    let snapshot: CritterProjectSnapshotV1 | null = null;
    try {
      snapshot = migrateProjectSnapshot(JSON.parse(await projectSnapshotFile.text()));
    } catch (error) {
      logger.warn(
        "[environmentBundle] Failed to parse critter_project.json; falling back to XML.",
        error
      );
    }

    if (snapshot) {
      const fileMap: Record<string, File> = {};
      files.forEach((file) => {
        const path = normalizeBundlePath(file.name);
        if (path) fileMap[path] = file;
      });

      if (projectManifest?.assets.length) {
        await validateProjectManifestAssets({ projectManifest, fileByNormalizedName });
      }
      warnIfProjectManifestIntegrityIsLocalOnly(projectManifest);
      const missingMeshAssets = await validateAndRegisterProjectMeshAssets({
        scene: snapshot.scene,
        projectManifest,
        fileByNormalizedName,
        persistAssets: options?.persistAssets,
      });
      if (missingMeshAssets.length > 0) {
        logger.warn("[environmentBundle] Project load blocked by missing mesh asset entries.", {
          count: missingMeshAssets.length,
          sample: missingMeshAssets.slice(0, 10),
        });
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("critter-toast", {
              detail: {
                message: `Project is missing ${missingMeshAssets.length} mesh asset registration${
                  missingMeshAssets.length === 1 ? "" : "s"
                }. Re-export the project or re-upload the missing mesh assets.`,
              },
            })
          );
        }
        throw new ProjectMissingMeshAssetsError(missingMeshAssets);
      }

      const modelXmlPath =
        projectManifest?.files.model || snapshot.canonical.modelXml || "model.xml";
      const modelXml =
        fileMap[modelXmlPath] ||
        fileMap[normalizeBundlePath(modelXmlPath)] ||
        fileByNormalizedName.get(normalizeBundlePath(modelXmlPath).toLowerCase()) ||
        fileByNormalizedName.get("model.xml") ||
        null;

      getSceneActions().restoreState(snapshot.scene);
      useFileStore.setState({
        fileMap,
        // Project snapshots are canonical. Keep model.xml available in fileMap for
        // export/physics, but do not arm App's xmlFile subscriber to reparse it.
        xmlFile: null,
        referencedFiles: snapshot.fileState.referencedFiles,
        missingFiles: [],
        keyframes: snapshot.fileState.keyframes,
        keyframeDetails: snapshot.fileState.keyframeDetails,
        preservedMjcfSections: snapshot.fileState.preservedMjcfSections,
        pendingMjcfImportWarnings: null,
        projectName: snapshot.project.name,
        isNewProject: false,
      });
      getProjectSaveActions().markProjectSaved(
        buildProjectSaveSnapshotHash({
          scene: getSceneState(),
          fileState: useFileStore.getState(),
        }),
        "import"
      );
      getHistoryActions().clearHistory();

      const sceneConfigPath = projectManifest?.files.sceneConfig || snapshot.canonical.sceneConfig;
      const sceneConfigFile =
        fileMap[sceneConfigPath] ||
        fileMap[normalizeBundlePath(sceneConfigPath)] ||
        fileByNormalizedName.get(normalizeBundlePath(sceneConfigPath).toLowerCase()) ||
        fileByNormalizedName.get("scene_config.json") ||
        null;
      if (sceneConfigFile) {
        try {
          const sceneConfig = migrateSceneConfig(JSON.parse(await sceneConfigFile.text()));
          if (sceneConfig) {
            applyCameraOrbitConfig({
              camera: asCameraConfig(sceneConfig.camera),
              orbit: asOrbitConfig(sceneConfig.orbit),
            });
            restoreGeomPairingsFromConfig(
              Array.isArray(sceneConfig.geomPairings) ? sceneConfig.geomPairings : null,
              "scene_config.json"
            );
            restoreVisibilityOverridesFromConfig(
              sceneConfig.visibilityOverrides,
              "scene_config.json"
            );
            restoreVisibilitySettingsFromConfig(
              sceneConfig.visibilitySettings,
              "scene_config.json"
            );
          }
        } catch (error) {
          logger.warn(
            "[environmentBundle] Failed to restore scene metadata from scene_config.json.",
            error
          );
        }
      }

      try {
        window.dispatchEvent(new Event("structure-changed"));
      } catch {
        // Best-effort UI notification; the restored scene state is already applied.
      }

      if (options?.startPhysics) {
        if (!modelXml) {
          throw new Error("Canonical Critter project bundle is missing its model.xml file.");
        }
        await startSimulationFromXmlFile({
          preserveActuatorControls: options?.preserveActuatorControls ?? false,
          xmlFile: modelXml,
        });
        try {
          window.dispatchEvent(new Event("physics-reset-controls"));
        } catch {
          // Best-effort UI notification after physics start.
        }
      }

      return { name: "environment", files: files.length };
    }
  }

  const hasModelXml = files.some((f) => {
    const normalized = normalizeBundlePath(f.name).toLowerCase();
    return normalized === "model.xml" || normalized.endsWith("/model.xml");
  });
  if (!hasModelXml) {
    throw new Error('Bundle must contain a "model.xml" file.');
  }

  // Load into app's fileStore + sceneStore using the existing XML pipeline.
  // We use the existing `handleFiles` by passing a simple "event-like" object.
  const fileActions = useFileStore.getState().actions;
  await fileActions.handleFiles({
    target: { files, value: "" },
  } as unknown as FileInputChangeEvent);

  if (options?.startPhysics) {
    // Start physics from the loaded XML verbatim (critical for exact playback fidelity).
    await startSimulationFromXmlFile({
      preserveActuatorControls: options?.preserveActuatorControls ?? false,
    });

    // Reset slider state so stale actuator keys don't linger across env swaps
    try {
      window.dispatchEvent(new Event("physics-reset-controls"));
    } catch {
      // Best-effort UI notification after physics start.
    }
  }

  return { name: "environment", files: files.length };
}

export type EnvironmentProjectArchiveArgs = {
  filenameBase?: string;
  extension?: "critter" | "critterrefs" | "zip";
  allowUnverifiableLocalProject?: boolean;
  unverifiableReason?: CritterProjectIntegrityReasonV1;
  originalIntegrityReason?: CritterProjectIntegrityReasonV1;
};

export type BuiltEnvironmentProjectArchive = {
  filename: string;
  zipBytes: Uint8Array;
  zipSha256: string;
  archiveEntries: Record<string, Uint8Array>;
  projectManifest: NonNullable<ReturnType<typeof buildProjectManifest>>;
};

export async function buildEnvironmentProjectArchive(
  args?: EnvironmentProjectArchiveArgs
): Promise<BuiltEnvironmentProjectArchive> {
  const filenameBase = args?.filenameBase || "critter_environment_bundle";
  const extension = args?.extension || "critter";
  const fileState = useFileStore.getState();
  const xml = await fileState.actions.generateXmlFromState({
    startupPosePolicy: "currentScenePose",
    xmlNamingMode: "uuid",
  });
  const sceneConfig = buildSceneConfig();
  const projectSnapshot = buildCurrentProjectSnapshot({ modelXmlPath: "model.xml" });
  const assetEntries: CritterProjectAssetManifestEntryV1[] = [];

  const zipEntries: Record<string, Uint8Array> = {
    "model.xml": strToU8(xml),
    "scene_config.json": strToU8(JSON.stringify(sceneConfig, null, 2)),
    [CRITTER_PROJECT_SNAPSHOT_FILENAME]: strToU8(JSON.stringify(projectSnapshot, null, 2)),
  };

  const fileMap = fileState.fileMap || {};
  for (const asset of await collectResolvedXmlAssetsForZip(xml, fileMap)) {
    if (!asset.zipPath || asset.zipPath.toLowerCase() === "model.xml") continue;
    let buf: ArrayBuffer;
    try {
      buf = await asset.file.arrayBuffer();
    } catch (error) {
      throw new ProjectAssetReadError(asset.zipPath, error);
    }
    assetEntries.push(
      await buildAssetManifestEntry(asset, buf, {
        allowUnverifiableLocalProject: args?.allowUnverifiableLocalProject,
      })
    );
    zipEntries[asset.zipPath] = new Uint8Array(buf);
  }
  const integrityComplete = assetEntries.every((asset) => Boolean(asset.sha256));
  const integrityReason = args?.allowUnverifiableLocalProject
    ? args.unverifiableReason || "user-override"
    : undefined;
  const projectManifest = buildProjectManifest({
    appVersion: APP_VERSION,
    projectName: fileState.projectName || "Untitled Scene",
    createdAtIso: await readProjectCreatedAtIsoFromFileMap(fileMap),
    projectSnapshotPath: CRITTER_PROJECT_SNAPSHOT_FILENAME,
    modelXmlPath: "model.xml",
    sceneConfigPath: "scene_config.json",
    assetsRoot: "assets/",
    assets: assetEntries,
    integrity: integrityComplete
      ? { policy: "sha256-required", complete: true }
      : {
          policy: "best-effort",
          complete: false,
          reason: integrityReason || "user-override",
          ...(args?.originalIntegrityReason
            ? { details: { originalReason: args.originalIntegrityReason } }
            : {}),
        },
  });
  zipEntries[CRITTER_PROJECT_MANIFEST_FILENAME] = strToU8(JSON.stringify(projectManifest, null, 2));

  // Hash the archive entries before fflate writes the outer zip. Manifest asset
  // hashes are over decompressed logical files; zipSha256 is the transport file hash.
  const zipBytes = zipSync(zipEntries, { level: 0 });
  const zipSha256 = await sha256Hex(zipBytes);
  return {
    filename: `${filenameBase}.${extension}`,
    zipBytes,
    zipSha256,
    archiveEntries: zipEntries,
    projectManifest,
  };
}

export async function downloadEnvironmentZip(args?: EnvironmentProjectArchiveArgs) {
  const built = await buildEnvironmentProjectArchive(args);
  const zipBuffer = built.zipBytes.buffer.slice(
    built.zipBytes.byteOffset,
    built.zipBytes.byteOffset + built.zipBytes.byteLength
  ) as ArrayBuffer;
  downloadBlob(built.filename, new Blob([zipBuffer], { type: "application/zip" }));
}

export async function loadEnvironmentZip(
  file: File,
  options?: {
    startPhysics?: boolean;
    preserveActuatorControls?: boolean;
    persistAssets?: boolean;
    allowReadOnlyLoad?: boolean;
  }
): Promise<{ name: string; files: number }> {
  if (!file) throw new Error("No file provided");

  const zipBytes = await readZipFileWithinBrowserImportLimits(file);
  const entries = unzipSync(zipBytes);

  const files: File[] = [];
  const keys = Object.keys(entries || {});
  for (const name of keys) {
    const bytes = entries[name];
    if (!bytes || !bytes.length) continue;
    // skip directory entries when present
    if (name.endsWith("/")) continue;
    if (!isSafeRelativeArchivePath(name)) {
      throw new Error(`Archive contains unsafe file path: ${name}`);
    }
    const isXml = name.toLowerCase().endsWith(".xml");
    const mime = isXml ? "application/xml" : "application/octet-stream";
    const fileBuffer = bytes.buffer.slice(
      bytes.byteOffset,
      bytes.byteOffset + bytes.byteLength
    ) as ArrayBuffer;
    files.push(new File([fileBuffer], name, { type: mime }));
  }

  const result = await loadEnvironmentFiles(files, options);
  return { ...result, name: file.name };
}

export async function loadEnvironmentFolder(
  fileList: FileList | File[],
  options?: {
    startPhysics?: boolean;
    preserveActuatorControls?: boolean;
    persistAssets?: boolean;
    allowReadOnlyLoad?: boolean;
  }
): Promise<{ name: string; files: number }> {
  const rawFiles = Array.from(fileList || []) as File[];
  if (!rawFiles.length) throw new Error("No files provided");

  const rawPaths = rawFiles.map((f) =>
    normalizeBundlePath((f as FileWithRelativePath).webkitRelativePath || f.name)
  );
  const strippedPaths = stripSingleTopLevelDir(rawPaths);

  const files: File[] = rawFiles
    .map((f, i) => {
      const name = toSafeZipPath(strippedPaths[i] || f.name);
      if (!name) return null;
      if (name === f.name) return f;
      // Re-wrap the blob but with a normalized "bundle path" as the name.
      return new File([f], name, { type: f.type || "application/octet-stream" });
    })
    .filter(Boolean) as File[];

  const result = await loadEnvironmentFiles(files, options);
  const firstRelativePath = (rawFiles[0] as FileWithRelativePath)?.webkitRelativePath;
  const displayName = firstRelativePath
    ? String(firstRelativePath).split("/")[0] || "environment"
    : "environment";
  return { ...result, name: displayName };
}

export async function loadEnvironmentBundle(
  input: File | FileList | File[],
  options?: {
    startPhysics?: boolean;
    preserveActuatorControls?: boolean;
    persistAssets?: boolean;
    allowReadOnlyLoad?: boolean;
  }
): Promise<{ name: string; files: number }> {
  if (!input) throw new Error("No input provided");

  // Single file: if it's an archive, treat as a bundle; otherwise treat as a folder-like file list.
  if (input instanceof File) {
    const lowerName = input.name.toLowerCase();
    const isArchive =
      lowerName.endsWith(".zip") ||
      lowerName.endsWith(".critter") ||
      lowerName.endsWith(".critterrefs");
    if (isArchive) return await loadEnvironmentZip(input, options);
    return await loadEnvironmentFolder([input], options);
  }

  const files = Array.from(input) as File[];
  if (!files.length) throw new Error("No files provided");

  // If a single archive came through a multi-picker, load it as a bundle.
  if (
    files.length === 1 &&
    (files[0].name.toLowerCase().endsWith(".zip") ||
      files[0].name.toLowerCase().endsWith(".critter") ||
      files[0].name.toLowerCase().endsWith(".critterrefs"))
  ) {
    return await loadEnvironmentZip(files[0], options);
  }

  return await loadEnvironmentFolder(files, options);
}
